import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense, lazy } from "react";
import { HomePage } from "./HomePage";
import StaticPage from "./StaticPage";
import { apiUrl } from "./utils/api";

// Core admin components
import ProtectedRoute from "./admin/ProtectedRoute";
import PortalEntry from "./admin/PortalEntry";
import AdminLayout from "./admin/AdminLayout";

// Sub-pages
const DashboardContent = lazy(() => import("./admin/DashboardContent"));
const ProductsPage = lazy(() => import("./admin/ProductsPage"));
const OrdersPage = lazy(() => import("./admin/OrdersPage"));
const CustomersPage = lazy(() => import("./admin/CustomersPage"));
const AnalyticsPage = lazy(() => import("./admin/AnalyticsPage"));
const PaymentPage = lazy(() => import("./admin/PaymentPage"));
const SettingsPage = lazy(() => import("./admin/SettingsPage"));
const PagesPage = lazy(() => import("./admin/PagesPage"));

const App = () => {
  const location = useLocation();
  const [adminPath, setAdminPath] = useState<string>(localStorage.getItem("cached_admin_path") || "admin");
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(apiUrl("/api/v1/common/config"));
        if (response.ok) {
          const result = await response.json();
          const path = result.data?.admin_path?.trim().replace(/^\/+|\/+$/g, '') || "admin";
          localStorage.setItem("cached_admin_path", path);
          setAdminPath(path);
        }
      } catch (err) {
        console.warn("App: Using cached admin path");
      } finally {
        setIsConfigLoaded(true);
      }
    };
    fetchConfig();
  }, []);

  // Split path to detect admin prefix
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentPathPrefix = pathSegments[0] || "";
  
  // Important: We match against the state 'adminPath'
  const isEnteringAdmin = currentPathPrefix === adminPath;

  // To prevent "HomePage" flashing when we are trying to access a custom admin path 
  // that we don't know yet because config is still loading.
  if (!isConfigLoaded && !isEnteringAdmin && currentPathPrefix !== "") {
    // If it's not the root and we are still loading, show a neutral state
    return null;
  }

  return (
    <Routes>
      {/* 
        We use a conditional rendering strategy to prevent route hijacking.
        If we are on an admin-related URL, we render the admin route tree.
        Otherwise, we render the store route tree.
      */}
      {isEnteringAdmin ? (
        <Route path={`${adminPath}`}>
          <Route index element={<PortalEntry />} />
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Suspense fallback={null}><DashboardContent /></Suspense>} />
            <Route path="products" element={<Suspense fallback={null}><ProductsPage /></Suspense>} />
            <Route path="orders" element={<Suspense fallback={null}><OrdersPage /></Suspense>} />
            <Route path="customers" element={<Suspense fallback={null}><CustomersPage /></Suspense>} />
            <Route path="pages" element={<Suspense fallback={null}><PagesPage /></Suspense>} />
            <Route path="analytics" element={<Suspense fallback={null}><AnalyticsPage /></Suspense>} />
            <Route path="payments" element={<Suspense fallback={null}><PaymentPage /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={null}><SettingsPage /></Suspense>} />
            {/* Fallback for admin subroutes */}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Route>
        </Route>
      ) : (
        <>
          <Route path="/" element={<HomePage />} />
          <Route path="/:slug" element={<StaticPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
};

export default App;
